import CircularProgress from "@mui/material/CircularProgress"
import { PrimaryButton } from "components/Inputs/Button"
import { CountryCode, countryData } from "DataMapper/CountryCode"
import { FunctionComponent, useEffect, useRef, useState } from "react"
import OtpOnCall from "components/Login/OtpOnCall"
import { IIvrMessage } from "components/Login/index"
import { isMobile } from "react-device-detect"

interface Props {
  otp: Array<any>
  phone: string
  otpText: string
  otpErrMsg: string
  otpSuccessMsg: string
  country: CountryCode
  changeView: (view: string) => void
  submitOtp: () => Promise<void>
  resendOtp: () => void
  handleChange: (val: Array<any>) => void
  ivrCallRequest: () => void
  ivrMsg: IIvrMessage
  setIvrMsg: (value: IIvrMessage) => void
}

const EnterOtp: FunctionComponent<Props> = ({
  otp,
  phone,
  otpText,
  otpErrMsg,
  otpSuccessMsg,
  country,
  changeView,
  submitOtp,
  resendOtp,
  handleChange,
  ivrCallRequest,
  ivrMsg,
  setIvrMsg,
}) => {
  const [resendOtpCount, setResendOtpCount] = useState<number>(30)
  const resendOtpCountRef = useRef(resendOtpCount)
  const [submitLock, setSubmitLock] = useState(false)
  const otpWaiting =
    otp?.filter((n) => n)?.length === 6 && !otpErrMsg && !otpSuccessMsg
  const otpDivRef = useRef<HTMLInputElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleInputFocus = () => {
    if (isMobile && otpDivRef.current) {
      // Store the timeout ID in the ref so we can clear it later
      timeoutRef.current = setTimeout(() => {
        otpDivRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        })
      }, 300)
    }
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const otpInterval = setInterval(() => {
      if (resendOtpCountRef.current < 1) {
        clearInterval(otpInterval)
      } else {
        resendOtpCountRef.current -= 1
        setResendOtpCount(resendOtpCountRef.current)
      }
    }, 1000)
    return () => {
      clearInterval(otpInterval)
    }
  }, [])

  const getOtpMsg = () => {
    if (resendOtpCount) {
      return (
        <div className={`flex justify-center w-full mb-4 mt-8`}>
          <span className="text-[#1C2233]">Didn't get OTP?</span>
          <span className="text-[#656E7F] ml-1">
            {" "}
            Resend OTP in{" "}
            {resendOtpCount < 10
              ? `00:0${resendOtpCount}`
              : `00:${resendOtpCount}`}
          </span>
        </div>
      )
    } else if (otpSuccessMsg) {
      return (
        <p className="font-semibold lfc-text-success-500 mt-8 mb-4">
          {otpSuccessMsg}
        </p>
      )
    } else {
      return (
        <div className="flex justify-center w-full mt-8 mb-4">
          <span className="text-[#1C2233]">Didn't get OTP?</span>
          <span
            onClick={resendOtp}
            className="text-[#443EFF] font-semibold cursor-pointer ml-1 flex items-center"
          >
            Resend OTP
          </span>
        </div>
      )
    }
  }

  const otpRef = useRef<any>([])

  useEffect(() => {
    otpRef.current[0].focus()
  }, [])

  useEffect(() => {
    if (otp?.filter((n) => n)?.length === 6) {
      submitAndUnsetLock()
    }
  }, [otp])

  const handleOTP = (event: any, index: number) => {
    const newOTP = [...otp]
    const value = event.target.value
    if (!isNaN(value) && value.length <= 6 - index) {
      if (value.length === 1) {
        newOTP[index] = value
        if (index < 5) {
          otpRef.current[index + 1].focus()
        }
      } else if (value.length > 1 && value.length <= 6) {
        let offset = index
        while (offset < index + value.length) {
          newOTP[offset] = value[offset - index]
          if (offset + 1 <= 5) {
            otpRef.current[offset + 1].focus()
          }
          offset += 1
        }
      }
      handleChange(newOTP)
    }
  }

  const submitAndUnsetLock = () => {
    if (!submitLock) {
      setSubmitLock(true)
      submitOtp().finally(() => {
        setSubmitLock(false)
      })
    }
  }

  const handleDelete = (event: any, index: number) => {
    if (event.keyCode === 8) {
      const newOTP = [...otp]
      newOTP[index] = ""
      handleChange(newOTP)
      if (index > 0) {
        otpRef.current[index - 1].focus()
      }
    }
    if (
      event.key === "Enter" &&
      otp?.filter((n) => n)?.length === 6 &&
      !submitLock
    ) {
      submitAndUnsetLock()
    }
  }

  const checkOTPBoxColor = () => {
    if (otp?.filter((n) => n)?.length === 6 && otpErrMsg.length > 0)
      return "border-[#FF695B]"
    else if (otp?.filter((n) => n)?.length === 6 && otpSuccessMsg.length > 0)
      return "border-[#4AAC64]"
    else return "border-[#C6CBD2]"
  }

  useEffect(() => {
    if (otp?.filter((n) => n)?.length === 6 && otpErrMsg) {
      otpRef.current.forEach((input: HTMLInputElement) => {
        if (input) input.blur()
      })
    }
  }, [otpErrMsg, otp])

  return (
    <>
      <div className="flex justify-center">
        <div
          className="text-[#757575] leading-5 pb-4 -mt-5 flex justify-between w-full"
          style={{
            marginBottom: "16px",
          }}
        >
          <p className="text-lg text-[#1C2233]">
            OTP sent to{" "}
            <span className="font-bold">
              +{countryData[country as CountryCode].code} {phone}
            </span>
          </p>

          <button
            className="text-[#443EFF] cursor-pointer border-none bg-transparent flex items-center gap-0.5 px-1"
            onClick={() => changeView("phone")}
          >
            <img
              src="/assets/edit_pencil.svg"
              alt="edit"
              width={20}
              height={20}
            />
            Edit
          </button>
        </div>
      </div>

      <div
        className="flex justify-center items-center gap-3 md:gap-4"
        ref={otpDivRef}
      >
        {[...Array(6)].map((_i, index) => {
          return (
            <input
              className={`text-base w-[2.8rem] h-12 text-center rounded-lg border border-solid ${checkOTPBoxColor()} text-[#333] bg-white outline-none p-[14px_10px] focus:border-[#574EDD] focus:shadow-[0_0_0_1px_rgba(87,78,221,0.1)] md:text-lg`}
              key={index}
              type="number"
              placeholder=""
              value={otp[index]}
              onChange={(e) => handleOTP(e, index)}
              ref={(el) => (otpRef.current[index] = el)}
              onKeyDown={(event) => {
                const isDigit = /^[0-9]$/.test(event.key)
                const allowedKeys = [
                  "Backspace",
                  "ArrowLeft",
                  "ArrowRight",
                  "Tab",
                  "Delete",
                ]

                if (!isDigit && !allowedKeys.includes(event.key)) {
                  event.preventDefault()
                }

                handleDelete(event, index)
              }}
              onFocus={handleInputFocus}
            />
          )
        })}
      </div>

      {otpErrMsg && otp?.filter((n) => n)?.length === 6 && (
        <p className="text-sm leading-[18px] pt-3 w-full text-[#FF695B] text-center">
          {otpErrMsg}
        </p>
      )}

      {otpWaiting && (
        <div className="text-[#686868] text-sm mt-4 flex items-center justify-center gap-2">
          <div className="w-4 h-4">
            <CircularProgress size={16} />
          </div>
          Please wait
        </div>
      )}

      {getOtpMsg()}

      {country === CountryCode.INDIA && (
        <div className="pt-2 pb-10">
          <OtpOnCall
            onClick={ivrCallRequest}
            ivrMsg={ivrMsg}
            setIvrMsg={setIvrMsg}
          />
        </div>
      )}
    </>
  )
}

export default EnterOtp
